import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Card } from "primereact/card";
import BreadCrumbCustom from "../bread-crumb/bread-crumb";
import { BreadcrumbItem } from "../../interfaces/common/common";
import { Image } from "primereact/image";
import { Button } from "primereact/button";
import RouteConstant from "../../constants/route.constants";
import { Suspense, lazy, useEffect, useRef } from "react";
import AppLoading from "../../components/app-loading/app-loading";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ProductCheckoutProceedModel } from "../../models/products/product-checkout-proceed-model";
import { PaymentModel } from "../../models/payments/payment-model";
import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";

const PaymentMode = lazy(() => import("./payment-mode"));
const ProductNotifyDialog = lazy(
  () => import("../products/product-notify-dialog")
);
const OrderConfirmDialog = lazy(() => import("../orders/order-details-dialog"));
const PaymentConfirmDialog = lazy(() => import("../orders/payment-details-dialog"));

const Payment = () => {
  var navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [paymentMode, setPaymentMode] = useState<PaymentModel>();
  const [notifyHeader, setNotifyHeader] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [isNotifyVisible, setIsNotifyVisible] = useState(false);
  const selectedPaymentMode = useAppSelector((state) => state.payments);
  const isAdmin = useAppSelector((state) => state.isAdmin);
  const selectedAdminUserId = useAppSelector((state) => state.adminUser.selectedAdminUserId);

  const [isConfirmDialog, setIsConfirmDialog] = useState(false);
  const [orderId, setOrderId] = useState(0);
  const [PlutusTransactionReferenceID, setPlutusTransactionReferenceID] = useState<number>(0);
  const [cartDataState, setCartDataState] = useState<ProductCheckoutProceedModel | null>(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  useEffect(() => {
    const data = ProductStorageHelpers.GetProcessedProductStorage();
    if (data) setCartDataState(data);
  }, []);

  const breadCrumbItems: BreadcrumbItem = {
    items: [{ label: t("payment_breadcrumb_label") }],
    home: { label: t("common_breadcrumb_home") },
    backgroundImage: process.env.PUBLIC_URL + "/assests/images/image-1.jpeg",
    title: t("payment_breadcrumb_label"),
  };

  const getOrderSummary = async () => {
    if (isPlacingOrder) return;
    setIsPlacingOrder(true);
    try {
      if (loadPaymentOption()) {
        const isFromOrderSummary = localStorage.getItem("pending-points-used") !== null;
        if (isFromOrderSummary) {
          // Place the order directly
          const points = Number(localStorage.getItem("pending-points-used" ) || "0");
          const ratio = Number(localStorage.getItem("pending-reward-ratio" ) || "0");
          const updatedCartData = ProductStorageHelpers.GetProcessedProductStorage();
          if (updatedCartData) {
            const finalPayload = {
              ...updatedCartData,
              rewardPointsUsed: points,
              rewardToCurrencyRatio: ratio
            };

            const isExecutive = false; // Add executive check if needed, defaulting false for brevity
            let response;
            if (isAdmin || isExecutive) {
              response = await (await import("../../services/order/order.service")).default.setOrderForCustomer(
                finalPayload,
                selectedAdminUserId
              );
            } else {
              response = await (await import("../../services/order/order.service")).default.setOrderFromCustomer(finalPayload);
            }

            if (response.isSuccess) {
              setOrderId(response.id);
              setPlutusTransactionReferenceID(response.plutusTransactionReferenceID);
              
              const modeName = updatedCartData.paymentOption?.mode;
              if (
                modeName === "Cash On Delivery" ||
                modeName === "Payment Link" ||
                modeName === "Card" ||
                modeName === "UPI" ||
                response.paymentAmount <= 0
              ) {
                setCartDataState({
                  ...updatedCartData,
                  plutusTransactionReferenceId: response.plutusTransactionReferenceID,
                  amount: response.paymentAmount,
                  rewardPointsUsed: points,
                  rewardToCurrencyRatio: ratio
                });
                setIsConfirmDialog(true);
              } else if (modeName === "Online Payment" && response.paymentAmount > 0) {
                const getwayData = response.additionalInfo;
                const { selectedGetway } = await import("../../redux/slices/getway-link");
                dispatch(selectedGetway({
                  paymentURL: getwayData.paymentURL,
                  expectedDeliveryDate: getwayData.expectedDeliveryDate
                }));
                navigate(RouteConstant.getwayProcess);
              }
              
              ProductStorageHelpers.ClearAllLocalStorage();
              localStorage.removeItem("processed-items");
              localStorage.removeItem("checkout-items");
              localStorage.removeItem("courier-provider");
              localStorage.removeItem("courier-charges");
              localStorage.removeItem("total-weight");
              localStorage.removeItem("pending-points-used");
              localStorage.removeItem("pending-reward-ratio");
            } else {
              // Error handling
              navigate(RouteConstant.products);
            }
          }
        } else {
          navigate(RouteConstant.orderSummary);
        }
      } else {
        setNotifyHeader("Service Unavailable");
        setNotifyMessage(
          "Please select 'Cash on Delivery' as the payment mode. Other payment modes are still a work in progress."
        );
        setIsNotifyVisible(true);
      }
    } catch (error) {
      console.error("Failed to place order:", error);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const loadPaymentMode = (value: any) => {
    setPaymentMode(value);
  };

  const loadPaymentOption = () => {
    const orderedData = ProductStorageHelpers.GetProcessedProductStorage();
    if (orderedData !== null) {
      let data: ProductCheckoutProceedModel = orderedData;
      if (paymentMode !== undefined) {
        data.paymentOption.id = paymentMode!.id.toString();
        data.paymentOption.mode = paymentMode!.mode;
        data.paymentOption.code = paymentMode!.code;
        data.paymentOption.status = paymentMode!.mode;
        data.paymentOption.description =
          paymentMode != null ? paymentMode.mode : "Cash on Delivery";
        data.paymentOption.dateTime = Date.now.toString();
      } else {
        data.paymentOption.id =
          selectedPaymentMode.selectedPayment!.id.toString();
        data.paymentOption.mode = selectedPaymentMode.selectedPayment!.mode;
        data.paymentOption.code = selectedPaymentMode.selectedPayment!.code;
        data.paymentOption.status = selectedPaymentMode.selectedPayment!.mode;
        data.paymentOption.description =
          selectedPaymentMode.selectedPayment!.mode != null
            ? selectedPaymentMode.selectedPayment!.mode
            : "Cash on Delivery";
        data.paymentOption.dateTime = Date.now.toString();
      }

      ProductStorageHelpers.SetProcessedProductStorage(data);
    }
    return true;
  };

  const hideNotifyDialog = () => {
    setIsNotifyVisible(false);
  };

  return (
    <>
      <Suspense fallback={<AppLoading />}>
        <BreadCrumbCustom
          items={breadCrumbItems.items}
          home={breadCrumbItems.home}
          backgroundImage={breadCrumbItems.backgroundImage}
          title={breadCrumbItems.title}
        ></BreadCrumbCustom>
        <div className="Address-form-container layout-content mt-3 mb-3  grid">
          <div className="col-12 p-inputgroup">
            <span>
              <h5 className="mobile-txt-fnt mb-0">
                <Link to={RouteConstant.orderSummary} className="text-primary font-bold hover:underline" style={{ textDecoration: 'none' }}>
                  <i className="pi pi-arrow-left mr-2"></i> Back to Order Summary
                </Link>
              </h5>
            </span>
          </div>
          <div className="col-12 md:col-8 lg:col-8 ">
            <Card
              className="user-address-card border-1 surface-border"
              title={t("user_your_payment")}
            >
              <div className="payment-mode-card">
                {/* <small className="p-warn">
                  2% payment Gateway charges will be included for all Non UPI
                  Payments.
                </small> */}
                <PaymentMode key="paymentmode" paymentmode={loadPaymentMode} />
              </div>
              <div className="flex flex-column gap-2 mt-4">
                {paymentMode?.mode !== "" && paymentMode?.mode !== undefined ? (
                  <Button
                    label="Place Order Now"
                    className="p-button-primary p-py-3 font-bold border-round-lg w-full text-lg shadow-2"
                    onClick={() => getOrderSummary()}
                    raised
                    icon="pi pi-shopping-bag"
                    disabled={!paymentMode || !paymentMode.mode || isPlacingOrder}
                    loading={isPlacingOrder}
                  />
                ) : null}
              </div>
            </Card>
          </div>

          <div className="md:col-4 flex bg-light-green justify-content-center  Auth-form-container img-none">
            <Image
              className="payment-image-right"
              src={process.env.PUBLIC_URL + "/assests/images/pay_mode.png"}
              alt="Image"
            />
          </div>
        </div>
        <ProductNotifyDialog
          messageHeader={notifyHeader}
          notifyMessage={notifyMessage}
          isVisible={isNotifyVisible}
          isHide={hideNotifyDialog}
        />
        {(cartDataState?.paymentOption?.mode === "Card" || cartDataState?.paymentOption?.mode === "UPI") && (cartDataState?.amount || 0) > 0 ?
          (<PaymentConfirmDialog
            showConfirmDialog={isConfirmDialog}
            hideConfirmDialog={() => { setIsConfirmDialog(false); navigate(RouteConstant.products); }}
            orderId={orderId}
            PlutusTransactionReferenceId={PlutusTransactionReferenceID}
            orderDetails={cartDataState}
            paymentModeType={cartDataState?.paymentOption.mode}
            amount={cartDataState.amount}
          />) :
          cartDataState ? (
            <OrderConfirmDialog
              showConfirmDialog={isConfirmDialog}
              hideConfirmDialog={() => { setIsConfirmDialog(false); navigate(RouteConstant.products); }}
              orderDetails={cartDataState}
              orderId={orderId}
              showGetwayPage={() => { setIsConfirmDialog(false); navigate(RouteConstant.getwayProcess); }}
            />
          ) : null
        }
      </Suspense>
    </>
  );
};

export default Payment;